// CoverSelectionModal.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  Button,
  Card,
  message,
  Typography,
  Alert,
  Tag,
} from 'antd';
import { WarningOutlined, EyeOutlined, QuestionCircleOutlined } from '@ant-design/icons'; // UPDATED
import CoverVariationsModal from './CoverVariationsModal';
import AppTour, { renderAppTourPanel } from './tours/AppTour';
import FeatherSpinner from './components/FeatherSpinner';
import { resolveFrontendApiBase } from './utils/apiBase';
import { getAuthHeaders } from './utils/authHeaders';
import './CoverModalRefresh.css';

const { Title, Text } = Typography;

const API_BASE = resolveFrontendApiBase();

interface CoverSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  generatedImages: string[];
  targetLanguage: string;
  bookLanguageName?: string;
  onSaveCovers: (covers: { [lang: string]: string | null }) => void;
  bookId: string;
  userId: string;
  bookTitle: string;
  translatedTitle?: string;
  seriesName?: string;
  translatedSeriesName?: string;
  authorName?: string;
  currentCoverUrl?: string;
  generationOptions?: {
    mode: 'modify' | 'generate';
    replaceTitle?: boolean;
    replaceSeries?: boolean;
    addLanguageSticker?: boolean;
    authorMode?: 'include' | 'omit';
    additionalReplacements?: Array<{ source: string; replacement: string }>;
  };
  creditBalance?: { credit_balance: number } | null;
  getCoverCreditCost?: (args: { mode: 'modify' | 'generate'; imageCount: number }) => number;
  regenerateProvider?: 'gemini' | 'openai';
  regeneratePrompt?: string;
  onRequestRegenerate?: (language: string) => void;
  onVariationsStart?: (language: string) => void;
  onVariationsComplete?: (images: string[]) => void;
  variationSeedImage?: string;
  // Optional: expected number of images requested in generation
  expectedImageCount?: number;
  // Optional: explicit number of failures if parent provides it (otherwise computed)
  failedCount?: number;
  // Optional: detailed failure messages to show to the user
  failureMessages?: string[];
}

const CoverSelectionModal: React.FC<CoverSelectionModalProps> = ({
  visible,
  onClose,
  generatedImages,
  targetLanguage,
  bookLanguageName,
  onSaveCovers,
  bookId,
  userId,
  bookTitle,
  translatedTitle,
  seriesName,
  translatedSeriesName,
  authorName,
  currentCoverUrl,
  generationOptions,
  creditBalance,
  getCoverCreditCost,
  regenerateProvider,
  regeneratePrompt,
  onRequestRegenerate,
  onVariationsStart,
  onVariationsComplete,
  variationSeedImage,
  expectedImageCount,
  failedCount,
  failureMessages,
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [images, setImages] = useState<string[]>(generatedImages || []);
  const [variationsOpen, setVariationsOpen] = useState(false);
  const [variationBase, setVariationBase] = useState<string | null>(null);
  const [stickerText, setStickerText] = useState<string | undefined>(undefined);
  
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // History persistence now happens server-side during generation.
  // Keep only minimal local state.
  useEffect(() => {
    setImages(generatedImages || []);
    setSelectedImage(null);
  }, [generatedImages]);

  useEffect(() => {
    if (visible) {
      setSelectedImage(null);
    }
  }, [visible]);

  // Persist/recover cover context (title/series/options) per book+language so re-open has info
  const normalizedLanguage = useMemo(
    () => ((targetLanguage === 'Original' ? 'original' : targetLanguage) || 'original').toLowerCase(),
    [targetLanguage]
  );
  const storageKey = useMemo(
    () => `poeai:coverContext:${bookId}:${normalizedLanguage}`,
    [bookId, normalizedLanguage]
  );
  const [persistedContext, setPersistedContext] = useState<any>(null);

  // Load persisted context on open
  useEffect(() => {
    if (!visible) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const ctx = JSON.parse(raw);
        setPersistedContext(ctx);
      }
    } catch (_e) {}
  }, [visible, storageKey]);

  // Persist latest props while modal is open (merge-safe)
  useEffect(() => {
    if (!visible) return;
    try {
      const prevRaw = sessionStorage.getItem(storageKey);
      const prev = prevRaw ? JSON.parse(prevRaw) : {};
      const mergedOptions = { ...(prev?.generationOptions || {}), ...(generationOptions || {}) };
      const pick = (a?: string, b?: string) => (a && a.trim() ? a : (b && b.trim() ? b : undefined));
      const ctx = {
        bookTitle: pick(prev?.bookTitle, bookTitle) || '',
        translatedTitle: pick(prev?.translatedTitle, translatedTitle),
        seriesName: pick(prev?.seriesName, seriesName),
        translatedSeriesName: pick(prev?.translatedSeriesName, translatedSeriesName),
        generationOptions: Object.keys(mergedOptions).length ? mergedOptions : prev?.generationOptions,
      };
      sessionStorage.setItem(storageKey, JSON.stringify(ctx));
      setPersistedContext(ctx);
    } catch (_e) {}
  }, [visible, bookTitle, translatedTitle, seriesName, translatedSeriesName, generationOptions, storageKey]);

  // Consider modal loading when opened with no images yet
  const isLoadingModal = visible && (images.length === 0 && !saving);
  const showLoadingOverlay = saving || isLoadingModal;
  const loadingTitle = saving ? 'Uploading cover' : 'Loading covers';
  const loadingCopy = saving
    ? 'Saving the selected cover to this book.'
    : 'Preparing generated cover options for review.';
  
  const handleSave = async (imageUrl: string): Promise<void> => {
    if (!imageUrl) {
      message.error('Invalid image');
      return;
    }

    setSaving(true);
    const normalizedLanguage = (targetLanguage === 'Original' ? 'original' : targetLanguage)?.toLowerCase();
    try {
      const response = await fetch(
        `${API_BASE}/api/upload-cover`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bookId,
            imageUrl,
            language: normalizedLanguage,
            userId,
          }),
        }
      );

      let data: any = null;
      let parsed = true;
      try {
        data = await response.json();
      } catch (_e) {
        parsed = false;
      }

      if (!response.ok) {
        const errMsg =
          (parsed && (data?.message || data?.detail)) ||
          response.statusText ||
          'Failed to save cover';
        message.error('Failed to save cover: ' + errMsg);
        return;
      }

      if (data?.status === 'success' && data?.coverUrl) {
        onSaveCovers({ [normalizedLanguage]: data.coverUrl });
        message.success('Cover saved successfully');
        onClose();
      } else {
        const errMsg = (parsed && (data?.message || data?.detail)) || 'Unknown error';
        message.error('Failed to save cover: ' + errMsg);
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      message.error('Error saving cover: ' + errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = (): void => {
    Modal.confirm({
      rootClassName: 'theme-aware-confirm',
      className: 'theme-aware-confirm',
      title: 'Discard Generated Covers?',
      icon: <WarningOutlined style={{ color: 'var(--accent-danger)' }} />,
      content: (
        <div>
          <p>Are you sure you want to discard all generated covers?</p>
          <Alert
            message="Credits spent on generating these images will not be refunded."
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Yes, Discard',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => {
        setSelectedImage(null);
        setImages([]);
        setVariationsOpen(false);
        onClose();
      },
    });
  };

  const isOriginal = (targetLanguage || '').toLowerCase() === 'original';
  const displayLanguage = isOriginal ? (bookLanguageName || 'Original') : targetLanguage;
  const generatedOptionImages = useMemo(
    () => (variationSeedImage ? images.filter((url) => url !== variationSeedImage) : images),
    [images, variationSeedImage]
  );
  const optionCount = generatedOptionImages.length;
  const hasReferencePanel = Boolean(currentCoverUrl || variationSeedImage);

  // Effective values (props merged with persisted context; avoid clobber by empty values)
  const prefer = (a?: string, b?: string) => (a && a.trim() ? a : (b && b.trim() ? b : undefined));
  const effOptions = useMemo(() => {
    const merged = { ...(persistedContext?.generationOptions || {}), ...(generationOptions || {}) };
    return Object.keys(merged).length ? merged : undefined;
  }, [generationOptions, persistedContext]);
  const effBookTitle = prefer(persistedContext?.bookTitle, bookTitle) || '';
  const effTranslatedTitle = prefer(persistedContext?.translatedTitle, translatedTitle);
  const effSeriesName = prefer(persistedContext?.seriesName, seriesName);
  const effTranslatedSeriesName = prefer(persistedContext?.translatedSeriesName, translatedSeriesName);

  // Resolve sticker text (from backend canonical mapping) when applicable (modify mode only)
  useEffect(() => {
    let cancelled = false;
    const needSticker = (effOptions?.mode === 'modify') && !!(effOptions?.addLanguageSticker) && targetLanguage !== 'Original';
    if (!visible || !needSticker) {
      setStickerText(undefined);
      return;
    }
    const lang = (targetLanguage || '').trim();
    if (!lang) { setStickerText(undefined); return; }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/cover/sticker-text?language=${encodeURIComponent(lang)}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!cancelled) setStickerText((data && data.stickerText) || `${lang} Edition`);
      } catch {
        if (!cancelled) setStickerText(`${lang} Edition`);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, effOptions?.mode, effOptions?.addLanguageSticker, targetLanguage]);

  // Partial generation failure awareness (backward-compatible if parent doesn't pass props)
  const expectedRequested = Math.max(0, expectedImageCount || 0);
  const receivedCount = optionCount;
  const computedFailed =
    typeof failedCount === 'number' ? failedCount : Math.max(0, expectedRequested - receivedCount);
  const showGenerationAlert = expectedRequested > 0 && computedFailed > 0;

  // -------------------- TOUR: refs/state/steps --------------------
  const infoRef = useRef<HTMLDivElement | null>(null);
  const currentCoverRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const firstPreviewBtnRef = useRef<HTMLButtonElement | null>(null);
  const firstVariationsBtnRef = useRef<HTMLButtonElement | null>(null);
  const firstUseBtnRef = useRef<HTMLButtonElement | null>(null);
  const regenBtnRef = useRef<HTMLButtonElement | null>(null);
  const discardBtnRef = useRef<HTMLButtonElement | null>(null);
  const alertRef = useRef<HTMLDivElement | null>(null);

  const [tourOpen, setTourOpen] = useState(false);
  const [flagsFetched, setFlagsFetched] = useState(false);
  const [allowAutoOpen, setAllowAutoOpen] = useState(false);

  // Fetch server-side UI flags for this tour; open once per user
  useEffect(() => {
    if (!visible) {
      setTourOpen(false);
      setFlagsFetched(false);
      setAllowAutoOpen(false);
      return;
    }
    if (!userId) return;

    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/user/ui-flags?userId=${encodeURIComponent(userId)}`, {
          headers: await getAuthHeaders(),
        });
        let seen = false;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          const flags = data?.uiFlags || {};
          seen = !!flags['coverSelectionTourSeen'];
        }
        if (mounted) {
          setAllowAutoOpen(!seen);
        }
      } catch (_e) {
        // non-blocking
      } finally {
        if (mounted) setFlagsFetched(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [visible, userId]);

  // Auto-open when ready (images rendered or not—steps skip missing targets)
  useEffect(() => {
    if (!visible || !flagsFetched || !allowAutoOpen) return;
    // Defer so refs settle
    const t = setTimeout(() => setTourOpen(true), 0);
    setAllowAutoOpen(false);
    return () => clearTimeout(t);
  }, [visible, flagsFetched, allowAutoOpen]);

  const handleCloseTour = async () => {
    setTourOpen(false);
    if (!userId) return;
    try {
      await fetch(`${API_BASE}/user/ui-flags/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          userId,
          flags: { coverSelectionTourSeen: true },
        }),
      });
    } catch (_e) {
      // non-blocking
    }
  };

  const openTourManually = () => setTourOpen(true);

const mkStep = (
  refEl: React.RefObject<HTMLElement>,
  title: string,
  description: React.ReactNode,
  placement?: any
) => ({
  title,
  description,
  // Lazy target: computed when Tour needs it, after layout is ready
  target: () => {
    const el = refEl.current as any;
    return el && typeof el?.getBoundingClientRect === 'function' ? el : null;
  },
  placement,
});

  const steps = [
    mkStep(
      infoRef,
      'Book details & what will change',
      <>
        This summary shows the original title/series and, when applicable, their translations. It mirrors the choices you made in the
        previous step (Generate vs. Translate and which fields to replace).
      </>
    ),
    currentCoverUrl
      ? mkStep(
          currentCoverRef,
          'Current cover (reference)',
          <>
            We keep your original cover handy for comparison. If you’re translating, verify layout and
            composition still read well after text changes.
          </>
        )
      : null,
    !isOriginal
      ? mkStep(
          alertRef,
          'Double-check translated text',
          <>
            Verify spelling and accents on all on-cover text. AI can make mistakes, especially with
            long words and diacritics.
          </>
        )
      : null,      
    mkStep(
      gridRef,
      'Pick from the generated options',
      images.length === 0 ? (
        <>
          No generated covers are available in this set. Generate new options to try again, or discard this set
          and adjust the prompt before another run.
        </>
      ) : (
        <>
          Click a card to select your preferred cover. Use the actions to preview, generate variations, or save your
          choice for this language.
        </>
      )
    ),
    mkStep(
      firstPreviewBtnRef as any,
      'Preview at full size',
      <>Open a large preview to check fine details, lettering, accents, and any artifacts.</>
    ),
    mkStep(
      firstVariationsBtnRef as any,
      'Generate variations',
      <>
        Like the vibe but want tweaks? Create near-neighbors from this option and review the new
        set back here.
      </>
    ),
    mkStep(
      firstUseBtnRef as any,
      'Use this cover',
      <>Set this as the active cover for <strong>{displayLanguage}</strong>. Cover History keeps the saved versions for this language so you can switch later.</>
    ),
    mkStep(
      regenBtnRef as any,
      'Generate new options',
      <>
        Start fresh and discard the current grid. (Credits already spent aren’t refunded.) We also
        remember your last prompt choices when you return to Generation.
      </>
    ),
    mkStep(
      discardBtnRef as any,
      'Discard all covers',
      <>Clear everything and close. Useful if you want to rethink the approach before another run.</>
    ),
  ].filter(Boolean) as any[];
  // ------------------ END TOUR ---------------------

  return (
    <Modal
      className="cover-selection-modal"
      title={
        <div className="cover-refresh-titlebar">
          <span className="cover-refresh-title">
            Select {displayLanguage} Cover
          </span>
          <Button
            type="link"
            size="small"
            icon={<QuestionCircleOutlined />}
            onClick={openTourManually}
            className="app-tour-trigger cover-refresh-titlebar__tour"
          >
            Take a Tour
          </Button>
        </div>
      }
      visible={visible}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width="min(1280px, calc(100vw - 48px))"
      footer={
        <div className="cover-refresh-footer">
          <div className="translation-modal-footer-summary cover-refresh-footer-summary" aria-live="polite">
            <span>
              <strong>{selectedImage ? 'Cover selected' : 'Select a cover'}</strong>
            </span>
            <span className="translation-modal-footer-summary__separator">·</span>
            <span>
              {optionCount} option{optionCount === 1 ? '' : 's'} for {displayLanguage}
            </span>
            {selectedImage ? (
              <>
                <span className="translation-modal-footer-summary__separator">·</span>
                <span>Will replace the active {displayLanguage} cover</span>
              </>
            ) : null}
          </div>
          <div className="cover-refresh-footer-actions">
            <Button
              key="save-selected"
              type="primary"
              disabled={!selectedImage || saving}
              loading={saving}
              onClick={() => {
                if (selectedImage) {
                  void handleSave(selectedImage);
                }
              }}
            >
              Use selected cover
            </Button>
            <Button
              key="regenerate"
              ref={regenBtnRef}
              disabled={saving}
              onClick={() => {
                Modal.confirm({
                  rootClassName: 'theme-aware-confirm',
                  className: 'theme-aware-confirm',
                  title: 'Generate New Options?',
                  icon: <WarningOutlined style={{ color: 'var(--accent-warning)' }} />,
                  content: (
                    <div>
                      <p>This will discard the current generated images shown in this dialog.</p>
                      <Alert
                        message="Credits already spent for these images will not be refunded."
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                      />
                    </div>
                  ),
                  okText: 'Yes, Generate',
                  cancelText: 'Cancel',
                  onOk: () => {
                    setSelectedImage(null);
                    setImages([]);
                    setVariationsOpen(false);
                    setVariationBase(null);
                    onClose();
                    try {
                      sessionStorage.setItem('poeai:suppressCoverAutoAiAssist', '1');
                    } catch (_e) {}
                    if (onRequestRegenerate) onRequestRegenerate(targetLanguage);
                  },
                });
              }}
            >
              Generate New Options
            </Button>
            <Button key="discard" ref={discardBtnRef} danger onClick={handleDiscard} disabled={saving} data-testid="tutorial:cover:discard-all">
              Discard All Covers
            </Button>
          </div>
        </div>
      }
    >
      <div className="cover-refresh-body" data-testid="tutorial:cover:selection-modal">
        <div className="cover-refresh-modal-stage cover-selection-modal-stage">
          {showLoadingOverlay && (
            <div className="cover-refresh-loading-overlay" role="status" aria-live="polite">
              <div className="cover-refresh-loading-card">
                <FeatherSpinner style={{ width: 96, height: 96 }} />
                <div className="cover-refresh-loading-title">{loadingTitle}</div>
                <p className="cover-refresh-loading-copy">{loadingCopy}</p>
              </div>
            </div>
          )}
          <div className={showLoadingOverlay ? 'cover-refresh-workflow is-loading' : 'cover-refresh-workflow'}>
        {/* Book Information Section */}
        <div
          ref={infoRef}
	          className="cover-refresh-block cover-selection-context"
	          data-testid="tutorial:cover:selection-context"
        >
          <div className="cover-refresh-block-header">
            <div>
              <h3>Selection context</h3>
              <p>Compare the current cover with the generated options and verify on-cover text before saving.</p>
            </div>
          </div>
          <div className="cover-selection-text-compare">
            <div className="cover-selection-text-column">
              <div className="cover-selection-text-column-title">Original cover text</div>
              <div className="cover-selection-text-row">
                <span className="translation-modal-kicker">Title</span>
                <strong>{effBookTitle || '—'}</strong>
              </div>
              {effOptions?.additionalReplacements?.map((row: { source: string; replacement: string }, index: number) => (
                <div className="cover-selection-text-row" key={`additional-source-${index}`}>
                  <span className="translation-modal-kicker">Text {index + 1}</span>
                  <strong>{row.source}</strong>
                </div>
              ))}
              {effSeriesName && (effOptions?.mode === 'generate' || effOptions?.replaceSeries) ? (
                <div className="cover-selection-text-row">
                  <span className="translation-modal-kicker">Series</span>
                  <strong>{effSeriesName}</strong>
                </div>
              ) : null}
            </div>
            {targetLanguage !== 'Original' ? (
              <div className="cover-selection-text-column">
                <div className="cover-selection-text-column-title">{targetLanguage} cover text</div>
                {effOptions?.additionalReplacements?.map((row: { source: string; replacement: string }, index: number) => (
                  <div className="cover-selection-text-row" key={`additional-target-${index}`}>
                    <span className="translation-modal-kicker">Text {index + 1}</span>
                    <strong>{row.replacement}</strong>
                  </div>
                ))}
                {effTranslatedTitle && (effOptions?.mode === 'generate' || effOptions?.replaceTitle) ? (
                  <div className="cover-selection-text-row">
                    <span className="translation-modal-kicker">Title</span>
                    <strong>{effTranslatedTitle}</strong>
                  </div>
                ) : null}
                {effTranslatedSeriesName && (effOptions?.mode === 'generate' || effOptions?.replaceSeries) ? (
                  <div className="cover-selection-text-row">
                    <span className="translation-modal-kicker">Series</span>
                    <strong>{effTranslatedSeriesName}</strong>
                  </div>
                ) : null}
                {effOptions?.mode === 'modify' && effOptions?.addLanguageSticker && stickerText ? (
                  <div className="cover-selection-text-row">
                    <span className="translation-modal-kicker">Sticker</span>
                    <strong>{stickerText}</strong>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {/* Note about translated covers text accuracy */}
        {!isOriginal && (
          <div ref={alertRef}>
            <Alert
              type="warning"
              showIcon
              message="Please verify spelling and accents on translated cover text."
              className="cover-selection-inline-alert"
            />
          </div>
        )}

        {/* Cover Selection Section */}
        <div className={`cover-selection-compare-layout ${hasReferencePanel ? 'has-current-cover' : ''}`}>
          {hasReferencePanel && (
            <aside ref={currentCoverRef} className="cover-selection-current-panel">
              {currentCoverUrl && (
                <>
                  <div className="cover-refresh-section-title">Current cover</div>
                  <Card
                    className="cover-refresh-option-card cover-refresh-option-card--reference"
                    bodyStyle={{ padding: 8 }}
                  >
                    <div className="cover-refresh-cover-frame cover-refresh-cover-frame--reference">
                      <Button
                        size="small"
                        shape="circle"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewUrl(currentCoverUrl);
                        }}
                        className="cover-refresh-icon-action"
                      />
                      <img
                        alt="Current Cover"
                        src={currentCoverUrl}
                      />
                    </div>
                    <div className="cover-refresh-option-title">
                      <Text type="secondary">
                        {isOriginal ? (bookLanguageName || 'Original') : 'Original'}
                      </Text>
                    </div>
                  </Card>
                </>
              )}
              {variationSeedImage && variationSeedImage !== currentCoverUrl && (
                <div className="cover-selection-seed-reference">
                  <div className="cover-selection-seed-thumb">
                    <img src={variationSeedImage} alt="Variation seed cover" />
                    <Button
                      size="small"
                      shape="circle"
                      icon={<EyeOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewUrl(variationSeedImage);
                      }}
                      className="cover-refresh-icon-action"
                    />
                  </div>
                  <div className="cover-selection-seed-copy">
                    <span className="translation-modal-kicker">Variation seed</span>
                    <strong>Reference only</strong>
                    <span>Used to generate these new options.</span>
                  </div>
                </div>
              )}
            </aside>
          )}

          <section className="cover-selection-options-panel" data-testid="tutorial:cover:selection-options">
            <div className="cover-refresh-section-header cover-selection-options-heading">
              <div className="cover-refresh-section-title">Generated options</div>
            </div>

            {showGenerationAlert && (
              <Alert
                role="status"
                aria-live="polite"
                type={receivedCount > 0 ? 'warning' : 'error'}
                showIcon
                message="Some covers failed to generate"
                description={
                  <div>
                    <div>
                      Requested {expectedRequested}, received {receivedCount}. {computedFailed}{' '}
                      {computedFailed === 1 ? 'image' : 'images'} {receivedCount > 0 ? 'failed to generate.' : 'did not generate.'}
                    </div>
                    {!!(failureMessages && failureMessages.length) && (
                      <ul style={{ margin: '8px 0 0 16px', padding: 0 }}>
                        {failureMessages.map((m, i) => (
                          <li key={i} style={{ fontSize: 12 }}>{m}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                }
                style={{ marginBottom: 12 }}
              />
            )}

            <div ref={gridRef}>
              {generatedOptionImages.length === 0 ? (
                <Alert
                  type="info"
                  showIcon
                  message="No images available"
                  description="No covers were generated. Click Generate New Options to try again, or adjust your prompt."
                />
              ) : (
                <div className="cover-selection-options-grid">
                  {generatedOptionImages.map((url, index) => (
                    <div key={index}>
	                      <Card
	                        hoverable
	                        onClick={() => setSelectedImage(url)}
	                        className={`cover-refresh-option-card ${selectedImage === url ? 'is-selected' : ''}`}
	                        bodyStyle={{ padding: 8 }}
	                        data-testid={index === 0 ? 'tutorial:cover:selection-option-card' : undefined}
	                      >
                        <div className="cover-refresh-cover-frame">
                          {selectedImage === url && (
                            <Tag className="cover-refresh-selected-tag">
                              Selected
                            </Tag>
                          )}
                          <Button
                            ref={index === 0 ? (firstPreviewBtnRef as any) : undefined}
                            size="small"
                            shape="circle"
                            icon={<EyeOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewUrl(url);
                            }}
                            className="cover-refresh-icon-action"
                          />
                          <img
                            alt={`Cover Option ${index + 1}`}
                            src={url}
                          />
                        </div>
                        <div className="cover-refresh-option-title">
                          <Text strong>Option {index + 1}</Text>
                        </div>
                        <div className="cover-refresh-option-actions">
	                          <Button
	                            ref={index === 0 ? (firstVariationsBtnRef as any) : undefined}
	                            block
	                            size="small"
	                            className="cover-refresh-wrap-button"
	                            data-testid={index === 0 ? 'tutorial:cover:open-variations' : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              setVariationBase(url);
                              setVariationsOpen(true);
                            }}
                          >
                            Generate variations
                          </Button>
	                          <Button
	                            ref={index === 0 ? (firstUseBtnRef as any) : undefined}
	                            block
	                            size="small"
	                            type="primary"
	                            loading={saving}
	                            className="cover-refresh-wrap-button"
	                            data-testid={index === 0 ? 'tutorial:cover:use-selected' : undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleSave(url);
                            }}
                          >
                            Use this cover
                          </Button>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Full-size preview modal */}
        <Modal
          className="cover-preview-modal"
          visible={!!previewUrl}
          footer={null}
          onCancel={() => setPreviewUrl(null)}
          width={1100}
          bodyStyle={{ padding: 0 }}
        >
          <div className="cover-preview-layout">
            <div className="cover-preview-image">
              <img alt="Cover preview" src={previewUrl || ''} />
            </div>
            <div className="cover-preview-details">
              <Title level={5} style={{ marginTop: 0 }}>Details</Title>
              <div>
                <Text type="secondary">Language:</Text> <span>{displayLanguage}</span>
              </div>
              {/* On-cover text inspection */}
              <div style={{ marginTop: 8 }}>
                <Text strong>On-cover Text</Text>
                <div style={{ marginTop: 4 }}>
                  <div>
                    <Text type="secondary">Original Title:</Text> <span>{effBookTitle}</span>
                  </div>
                  {effTranslatedTitle && targetLanguage !== 'Original' && (
                    <div>
                      <Text type="secondary">Translated Title:</Text> <span>{effTranslatedTitle}</span>
                    </div>
                  )}
                </div>
                {(effSeriesName || effTranslatedSeriesName) && (
                  <div style={{ marginTop: 4 }}>
                    {effSeriesName && (
                      <div>
                        <Text type="secondary">Original Series:</Text> <span>{effSeriesName}</span>
                      </div>
                    )}
                    {effTranslatedSeriesName && targetLanguage !== 'Original' && (
                      <div>
                        <Text type="secondary">Translated Series:</Text> <span>{effTranslatedSeriesName}</span>
                      </div>
                    )}
                  </div>
                )}
                {effOptions?.mode === 'modify' && effOptions?.addLanguageSticker && targetLanguage !== 'Original' && stickerText && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Sticker:</Text> <span>[{stickerText}]</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>

        {visible && (
          <CoverVariationsModal
            visible={variationsOpen}
            onClose={() => {
              setVariationsOpen(false);
              setVariationBase(null);
            }}
            baseImage={variationBase || ''}
            targetLanguage={targetLanguage}
            bookId={bookId}
            userId={userId}
            bookTitle={bookTitle}
            translatedTitle={translatedTitle}
            seriesName={seriesName}
            translatedSeriesName={translatedSeriesName}
            authorName={authorName}
            generationOptions={effOptions}
            creditBalance={creditBalance || null}
            getCoverCreditCost={getCoverCreditCost}
            onStart={(lang) => {
              // Ensure selection state and previous images are cleared when starting variations
              setSelectedImage(null);
              setImages([]);
              setVariationsOpen(false);
              setVariationBase(null);
              onClose();
              if (onVariationsStart) onVariationsStart(lang);
            }}
            onComplete={(imgs) => {
              if (onVariationsComplete) onVariationsComplete(imgs);
            }}
            onGenerated={(imgs) => {
              setImages(imgs);
              setSelectedImage(null);
              setVariationsOpen(false);
              setVariationBase(null);
            }}
          />
        )}

        {/* Guided Tour */}
        <AppTour
          open={tourOpen}
          onClose={handleCloseTour}
          steps={(steps || []).map((s: any) =>
            s
              ? {
                  ...s,
                  description: renderAppTourPanel(s.description, { ravenSize: 'lg' }),
                }
              : s
          )}
        />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CoverSelectionModal;
